import { useCallback } from "react";
import { Modal } from "@covertree/ui";
import { InfoDisclaimerBox } from "@covertree/ui/content";
import {
  AdditionalInterestFormBody,
  AdditionalInterestFormData as InterestFormData,
  useAdditionalInterestForm,
} from "@covertree/features";
import {
  AdditionalInterestType,
  PolicyUsage,
} from "@covertree/new-business-graphql";
import {
  selectPolicyUsage,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";

import { useCheckout } from "../CheckoutProvider";
import { getMaxInterests } from "./utils";
import { AdditionalInterestFormData } from "./form";

interface AdditionalInterestModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: AdditionalInterestFormData) => void;
  interestsCountByType: Record<AdditionalInterestType, number>;
  selectedInterest?: AdditionalInterestFormData | null;
}

export function AdditionalInterestModal({
  open,
  onClose,
  onSubmit,
  selectedInterest,
  interestsCountByType,
}: AdditionalInterestModalProps) {
  const isTenantFlow =
    useNewBusinessSelector(selectPolicyUsage) === PolicyUsage.Tenant;
  const { selectedPaymentPlanKey } = useCheckout();

  const handleSubmit = useCallback(
    (data: InterestFormData) =>
      onSubmit({
        ...data,
        // The validator rejects an empty type, so it is set by now.
        type: data.type as AdditionalInterestType,
        isBilling: data.isBilling ?? false,
      }),
    [onSubmit]
  );

  const interestForm = useAdditionalInterestForm({
    mode: selectedInterest ? "edit" : "add",
    onSubmit: handleSubmit,
    onClose,
    interestsCountByType,
    selectedInterest,
    maxInterests: getMaxInterests(selectedPaymentPlanKey, isTenantFlow),
    isTenantPolicy: isTenantFlow,
    isOpen: open,
  });

  return (
    <Modal
      open={open}
      onClose={interestForm.onCancel}
      title={selectedInterest ? "Edit Notification" : "Add Notification"}
      maxWidth="md"
      sx={{
        overflow: "visible",
        "&:focus": {
          outline: "none",
        },
      }}>
      <InfoDisclaimerBox
        dataTestId="continue-your-quote-modal"
        fontSize={16}
        mb={2}
        spacing={1.5}>
        Add interested parties here only if they need policy notifications and
        aren&apos;t paying the premium. If a mortgage company is paying, use the
        &quot;Mortgage Bill&quot; section instead
      </InfoDisclaimerBox>
      <AdditionalInterestFormBody {...interestForm} />
    </Modal>
  );
}
